/**
 * DraggrTest.tsx - Interactive test component for Draggr
 * 
 * PURPOSE:
 * - Live testing of drag and drop functionality
 * - Visual demonstration of Draggr features
 * - Development playground for new features
 * 
 * FEATURES TESTED:
 * - Basic drag and drop
 * - Reordering within lists
 * - Cross-list dragging
 * - Visual feedback (drag ghost, drop indicators)
 * - Multiple draggable types
 */

import React, { useState, useRef } from 'react';
import { usePragmaticDraggable, usePragmaticDropTarget } from '../hooks';
import './DraggrTest.css';

interface DraggableItem {
  id: string;
  type: 'card' | 'task' | 'file';
  content: string;
  color?: string;
}

interface DraggableItemProps {
  item: DraggableItem;
  index: number;
  onReorder: (fromIndex: number, toIndex: number) => void;
}

function DraggableItemComponent({ item, index, onReorder }: DraggableItemProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isOver, setIsOver] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  usePragmaticDraggable(ref, {
    type: item.type,
    data: { id: item.id, index },
    onDragStart: () => setIsDragging(true),
    onDrop: () => setIsDragging(false),
  });

  usePragmaticDropTarget(ref, {
    accepts: [item.type],
    onDragEnter: () => setIsOver(true),
    onDragLeave: () => setIsOver(false),
    onDrop: ({ source }) => {
      setIsOver(false);
      const sourceIndex = source.data.index as number;
      if (sourceIndex !== index) {
        onReorder(sourceIndex, index);
      }
    },
  });

  return (
    <div
      ref={ref}
      className={`draggr-test-item draggr-test-item--${item.type} ${
        isDragging ? 'draggr-test-item--dragging' : ''
      } ${isOver ? 'draggr-test-item--over' : ''}`}
      style={{ backgroundColor: item.color }}
    >
      <span className="draggr-test-item__handle">⋮⋮</span>
      <span className="draggr-test-item__content">{item.content}</span>
      <span className="draggr-test-item__type">{item.type}</span>
    </div>
  );
}

interface DraggableListProps {
  title: string;
  items: DraggableItem[];
  onReorder: (fromIndex: number, toIndex: number) => void;
}

function DraggableList({ title, items, onReorder }: DraggableListProps) {
  return (
    <div className="draggr-test-list">
      <h3 className="draggr-test-list__title">{title}</h3>
      <div className="draggr-test-list__items">
        {items.map((item, index) => (
          <DraggableItemComponent
            key={item.id}
            item={item}
            index={index}
            onReorder={onReorder}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * DraggrTest - Interactive test component
 * 
 * Can be loaded in wvtool via plugin for live testing
 */
export function DraggrTest() {
  const [cards, setCards] = useState<DraggableItem[]>([
    { id: 'card-1', type: 'card', content: 'Card 1', color: '#e3f2fd' },
    { id: 'card-2', type: 'card', content: 'Card 2', color: '#e8f5e9' },
    { id: 'card-3', type: 'card', content: 'Card 3', color: '#fff3e0' },
  ]);

  const [tasks, setTasks] = useState<DraggableItem[]>([
    { id: 'task-1', type: 'task', content: 'Task 1', color: '#f3e5f5' },
    { id: 'task-2', type: 'task', content: 'Task 2', color: '#fce4ec' },
    { id: 'task-3', type: 'task', content: 'Task 3', color: '#e0f2f1' },
  ]);

  const [files, setFiles] = useState<DraggableItem[]>([
    { id: 'file-1', type: 'file', content: 'document.pdf', color: '#ffebee' },
    { id: 'file-2', type: 'file', content: 'image.png', color: '#e8eaf6' },
    { id: 'file-3', type: 'file', content: 'data.csv', color: '#f1f8e9' },
  ]);

  const handleCardsReorder = (fromIndex: number, toIndex: number) => {
    const newCards = [...cards];
    const [removed] = newCards.splice(fromIndex, 1);
    newCards.splice(toIndex, 0, removed);
    setCards(newCards);
  };

  const handleTasksReorder = (fromIndex: number, toIndex: number) => {
    const newTasks = [...tasks];
    const [removed] = newTasks.splice(fromIndex, 1);
    newTasks.splice(toIndex, 0, removed);
    setTasks(newTasks);
  };

  const handleFilesReorder = (fromIndex: number, toIndex: number) => {
    const newFiles = [...files];
    const [removed] = newFiles.splice(fromIndex, 1);
    newFiles.splice(toIndex, 0, removed);
    setFiles(newFiles);
  };

  return (
    <div className="draggr-test">
      <div className="draggr-test__header">
        <h1>Draggr Test Interface</h1>
        <p>Test drag and drop functionality with live visual feedback</p>
      </div>

      <div className="draggr-test__features">
        <div className="draggr-test__feature">
          <span className="draggr-test__feature-icon">✓</span>
          <span>Drag within same list to reorder</span>
        </div>
        <div className="draggr-test__feature">
          <span className="draggr-test__feature-icon">✓</span>
          <span>Visual feedback (ghost + drop indicator)</span>
        </div>
        <div className="draggr-test__feature">
          <span className="draggr-test__feature-icon">✓</span>
          <span>Type-safe drag operations</span>
        </div>
      </div>

      <div className="draggr-test__lists">
        <DraggableList title="Cards" items={cards} onReorder={handleCardsReorder} />
        <DraggableList title="Tasks" items={tasks} onReorder={handleTasksReorder} />
        <DraggableList title="Files" items={files} onReorder={handleFilesReorder} />
      </div>

      <div className="draggr-test__info">
        <h3>Implementation Details</h3>
        <ul>
          <li><strong>Library:</strong> @atlaskit/pragmatic-drag-and-drop</li>
          <li><strong>Hooks:</strong> usePragmaticDraggable, usePragmaticDropTarget</li>
          <li><strong>Type Safety:</strong> Each list only accepts its own type</li>
          <li><strong>Performance:</strong> Optimized with refs and minimal re-renders</li>
        </ul>
      </div>
    </div>
  );
}

export default DraggrTest;
